import { ENV } from '../config/env.js';
import { localModeToken } from '../config/local-mode.js';

export type TokenProvider = () => Promise<string>;

export interface ApiClient {
  readonly baseUrl: string;
  request<T>(path: string, init?: RequestInit): Promise<T>;
  authHeader(): Promise<Record<string, string>>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Token-injecting fetch wrapper. Throws `ApiError` on non-2xx so caller
 * code can branch on status without parsing the response twice.
 *
 * Local mode (`ENV.localMode`): the `getToken` callback is replaced with a
 * stub returning `'local-mode'`. The local HTTP server ignores the
 * `Authorization` header (localhost-trust security model), but we keep the
 * header shape stable so the rest of the request pipeline is unchanged.
 */
export function createApiClient(getToken: TokenProvider, baseUrl = ENV.apiBaseUrl): ApiClient {
  const resolveToken: TokenProvider = ENV.localMode ? localModeToken : getToken;

  async function authHeader(): Promise<Record<string, string>> {
    const token = await resolveToken();
    return { Authorization: `Bearer ${token}` };
  }

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    const auth = await authHeader();
    for (const [k, v] of Object.entries(auth)) headers.set(k, v);
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    const res = await fetch(`${baseUrl}${path}`, { ...init, headers });
    if (!res.ok) {
      const body = await safeJson(res);
      throw new ApiError(res.status, body, `${res.status} ${res.statusText}`);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return { baseUrl, request, authHeader };
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
